/* rs-comparison-hotspots — custom-element host for the static comparison
   frame's hotspot dots + popout cards.

   Each instance owns its own dots (inside .rs-cs__frame) and cards
   (inside .rs-cs__cards). A dot and its card share a data-hs-index.
   Clicking a dot toggles its card; only one card is open at a time.

   No positioning logic lives here — a dot's/card's screen position is
   set entirely by the --hs-x/--hs-y (desktop) and --hs-xm/--hs-ym
   (mobile) CSS custom properties written inline by Liquid, and the
   flip-x/flip-y classes are computed in Liquid too. This module only
   toggles the [hidden] attribute + aria-expanded state and handles
   dismissal (close button, Escape, outside click).

   Mirrors the rs-pdp-compare.js shape: registration guard,
   connectedCallback self-wiring with a re-entry guard + a
   requestAnimationFrame wait (the section's <script defer> runs in
   <body>, where defer is a no-op), and disconnectedCallback that
   removes its own document-level listeners. */

(function () {
  if (customElements.get('rs-comparison-hotspots')) return;

  class RsComparisonHotspots extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      // Wait one frame so the section's children have parsed.
      requestAnimationFrame(() => this.wire());
    }

    disconnectedCallback() {
      if (this._rootClickHandler) {
        this.removeEventListener('click', this._rootClickHandler);
        this._rootClickHandler = null;
      }
      if (this._keydownHandler) {
        this.removeEventListener('keydown', this._keydownHandler);
        this._keydownHandler = null;
      }
      if (this._docClickHandler) {
        document.removeEventListener('click', this._docClickHandler, true);
        this._docClickHandler = null;
      }
    }

    wire() {
      // The index of the currently-open hotspot, or null when none is open.
      this._openIndex = null;

      // Delegated click on the host root: a click on a dot toggles that
      // index; a click on a card's close button closes the open card.
      this._rootClickHandler = (e) => {
        var closeBtn = e.target.closest ? e.target.closest('.rs-cs__card-close') : null;
        if (closeBtn) {
          this.close();
          return;
        }
        // Story dots open an rs-modal instead of a popover. They carry
        // [data-rs-modal-open] and deliberately NO [data-hs-index], so the
        // selector below cannot match them and only rs-modal.js's document
        // delegation acts — one handler per dot, decided in Liquid by
        // whether the block has a Story set. This branch exists purely so
        // an already-open popover card is dismissed before the dialog
        // covers it; without it the card would still be sitting there when
        // the modal is closed. It runs before rs-modal.js (this listener is
        // on the host, that one is on document, both bubble), so the close
        // completes ahead of the open.
        var storyDot = e.target.closest ? e.target.closest('[data-rs-modal-open]') : null;
        if (storyDot && this.contains(storyDot)) {
          this.close();
          return;
        }
        var dot = e.target.closest ? e.target.closest('.rs-cs__dot[data-hs-index]') : null;
        if (dot) {
          var i = dot.getAttribute('data-hs-index');
          if (this._openIndex === i) {
            this.close();
          } else {
            this.open(i);
          }
        }
      };
      this.addEventListener('click', this._rootClickHandler);

      // Escape closes and returns focus to the dot that opened the card
      // (mirrors global.js onKeyUpEscape's focus-return behaviour).
      this._keydownHandler = (e) => {
        if (e.key !== 'Escape' && e.key !== 'Esc') return;
        if (this._openIndex === null) return;
        var dot = this._dotFor(this._openIndex);
        this.close();
        if (dot) dot.focus();
      };
      this.addEventListener('keydown', this._keydownHandler);

      // Outside-click closes. Attach in capture phase, deferred one tick so
      // the click that opens a dot doesn't immediately self-dismiss it
      // (mirrors rs-menu.js's capture-phase + setTimeout(...,0) guard).
      this._docClickHandler = (e) => {
        if (this._openIndex === null) return;
        if (this.contains(e.target)) return;
        this.close();
      };
      setTimeout(() => {
        document.addEventListener('click', this._docClickHandler, true);
      }, 0);
    }

    _dotFor(index) {
      return this.querySelector('.rs-cs__dot[data-hs-index="' + index + '"]');
    }

    _cardFor(index) {
      return this.querySelector('.rs-cs__card[data-hs-index="' + index + '"]');
    }

    open(index) {
      // Close whatever is open first — only one card at a time.
      if (this._openIndex !== null && this._openIndex !== index) this.close();

      var dot = this._dotFor(index);
      var card = this._cardFor(index);
      if (!dot || !card) return;

      dot.setAttribute('aria-expanded', 'true');
      card.hidden = false;
      this._openIndex = index;
    }

    close() {
      if (this._openIndex === null) return;
      var dot = this._dotFor(this._openIndex);
      var card = this._cardFor(this._openIndex);
      if (dot) dot.setAttribute('aria-expanded', 'false');
      if (card) card.hidden = true;
      this._openIndex = null;
    }
  }

  customElements.define('rs-comparison-hotspots', RsComparisonHotspots);
})();
